import { useI18n } from "vue-i18n";
import { formatDistanceToNow, parseISO, format } from "date-fns";
import { cs, enUS } from "date-fns/locale";

/**
 * Composable for formatting last login timestamps.
 *
 * @example
 * const { formatLastLogin, formatLastLoginFull } = useLastLoginFormat();
 * const relativeTime = formatLastLogin("2024-01-15T10:30:00Z"); // "3 days ago"
 * const fullDate = formatLastLoginFull("2024-01-15T10:30:00Z"); // "15.01.2024 10:30"
 */
export function useLastLoginFormat() {
    const { t, locale } = useI18n();

    const getLocale = () => (locale.value === "cs" ? cs : enUS);

    /**
     * Format last login as relative time (e.g., "3 days ago")
     */
    const formatLastLogin = (dateString: string | null): string => {
        if (!dateString) {
            return t("kiosk.users.never_logged_in");
        }

        const date = parseISO(dateString);
        return formatDistanceToNow(date, {
            addSuffix: true,
            locale: getLocale(),
        });
    };

    /**
     * Format last login as full date/time for tooltips
     */
    const formatLastLoginFull = (dateString: string | null): string => {
        if (!dateString) {
            return t("kiosk.users.never_logged_in");
        }

        const date = parseISO(dateString);
        const formatPattern =
            locale.value === "cs" ? "d.M.yyyy HH:mm" : "MMM d, yyyy h:mm a";

        return format(date, formatPattern, {
            locale: getLocale(),
        });
    };

    return {
        formatLastLogin,
        formatLastLoginFull,
    };
}
